import React, { useState, useEffect, useRef } from 'react';
import WebFont from 'webfontloader';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';

const fontList = [
  'Default',  // Default option
  'Inter',    // Added Inter since it's heavily used in style presets
  'Roboto', 
  'Open Sans', 
  'Lato', 
  'Montserrat', 
  'Oswald',
  'Poppins',      
  'Raleway',      
  'Merriweather', 
  'Nunito',       
  'Ubuntu',       
  'PT Sans',      
  'Playfair Display', 
  'Dancing Script', 
  'Cinzel',        
  'Fira Sans',     
  'Crimson Text',  
  'Source Sans Pro', 
  'Bebas Neue',    
  'Rubik',         
  'Josefin Sans',  
  'Share Tech Mono',  // Added for retro style presets
  // Marvel/Superhero fonts
  'Orbitron',        // Perfect for cosmic/tech Marvel vibes
  'Russo One',       // Classic superhero comic book feel
  'Bungee',          // Modern superhero energy
  'Rajdhani',        // Sleek and powerful
  'Exo 2',           // Futuristic superhero
];

const CustomFontPicker = ({ label, subtitle, value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const [popperElement, setPopperElement] = useState(null);
  
  const { styles, attributes } = usePopper(buttonRef.current, popperElement, {
    placement: 'bottom-start',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 4], // 4px gap
        },
      },
      {
        name: 'preventOverflow',
        options: {
          padding: 8,
        },
      },
    ],
  });

  // Load all fonts except the default
  useEffect(() => {
    WebFont.load({
      google: {
        families: fontList.filter(font => font !== 'Default')
      },
    });
  }, []);

  const handleFontChange = (font) => {
    setIsOpen(false);
    if (font === 'Default') {
      onChange(null);
    } else {
      onChange(font);
    }
  };

  return (
    <div className="relative inline-block text-left w-full">
      {label && (
        <label className="block text-sm font-bold tracking-wide mb-2" style={{
          color: '#F1C40F',
          fontFamily: '"Share Tech Mono", monospace'
        }}>
          {label}
        </label>
      )}
      {subtitle && (
        <p className="mb-3 text-sm font-bold tracking-wide" style={{
          color: '#95A5A6',
          fontFamily: '"Share Tech Mono", monospace'
        }}>
          {subtitle}
        </p>
      )}
      <div>
        <button 
          ref={buttonRef}
          type="button"
          onClick={() => setIsOpen(!isOpen)} 
          className="relative block w-full rounded-md shadow-sm pl-4 pr-12 py-3 text-left cursor-pointer focus:outline-none focus:ring-2 sm:text-sm flex items-center justify-between transition-all duration-200 hover:shadow-md"
          style={{
            backgroundColor: 'rgba(44, 62, 80, 0.8)',
            border: '1px solid rgba(149, 165, 166, 0.3)',
            color: '#ECF0F1',
            fontFamily: '"Share Tech Mono", monospace'
          }}
        >
          <span style={{ fontFamily: value !== 'Default' ? value : '"Share Tech Mono", monospace' }}>
            {value || 'Inter'}
          </span>
          <span className="absolute right-4 flex items-center" style={{ color: '#95A5A6' }}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </span>
        </button>

        {isOpen && (
          <Portal>
            <div 
              className="fixed inset-0 bg-transparent"
              onClick={() => setIsOpen(false)}
            />
            <div 
              ref={setPopperElement}
              style={{
                ...styles.popper,
                backgroundColor: 'rgba(44, 62, 80, 0.95)',
                border: '1px solid rgba(149, 165, 166, 0.3)',
                backdropFilter: 'blur(10px)'
              }}
              {...attributes.popper}
              className="shadow-lg rounded-md py-1 text-base ring-1 ring-opacity-5 overflow-auto focus:outline-none sm:text-sm z-[9999]"
            >
              <div className="max-h-60 overflow-auto" style={{ width: buttonRef.current?.offsetWidth }}>
                {fontList.map((font, index) => (
                  <div
                    key={index}
                    className="cursor-pointer select-none relative py-3 pl-4 pr-4 transition-colors duration-150"
                    onClick={() => handleFontChange(font)}
                    style={{ 
                      fontFamily: font !== 'Default' ? font : '"Share Tech Mono", monospace',
                      color: '#ECF0F1'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = 'rgba(241, 196, 15, 0.1)';
                      e.target.style.color = '#F1C40F';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = '#ECF0F1';
                    }}
                  >
                    {font}
                  </div>
                ))}
              </div>
            </div>
          </Portal>
        )}
      </div>
    </div>
  );
};

const Portal = ({ children }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  return mounted ? createPortal(
    children,
    document.body
  ) : null;
};

export default CustomFontPicker;